import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import toast from "@/domains/toasts/helpers/toast";

export default function useGetHotelElevator(hotelId: number) {
  const { t } = useTranslation("hotel");

  const [isMyRoomLoading, setIsMyRoomLoading] = useState(false);
  const [myRoom, setMyRoom] = useState(null);
  const [isElevatorLoading, setIsElevatorLoading] = useState(false);
  const [data, setData] = useState(null);
  const [isEntering, setIsEntering] = useState(false);

  const onClose = useCallback(() => {
    setData(null);
    GameMpProvider.closeCurrentInterface(true);
  }, []);

  const loadMyRoom = useCallback(async () => {
    try {
      setIsMyRoomLoading(true);

      const roomResponse = await EstateMpProvider.getHotelRoomDetailsByHotelId(hotelId);

      if (roomResponse instanceof RpcMpProviderSuccessResponse) {
        if (roomResponse?.data && Object.keys(roomResponse.data).length > 0)
          setMyRoom(roomResponse.data);
      } else {
        console.log("getHotelRoomDetails:", roomResponse);
        toast.error(t("estate.errors.loadingRoomError"));
      }
    } catch (e) {
      console.log(e);
      toast.error(t("hotel.toast.loadingSystemError"));
    } finally {
      setIsMyRoomLoading(false);
    }
  }, [hotelId, t]);

  const load = useCallback(async () => {
    setIsElevatorLoading(true);

    await EstateMpProvider.getHotelElevator(hotelId)
      .then((response) => {
        if (response instanceof RpcMpProviderSuccessResponse) {
          setData(response.data);
        } else {
          console.error(response);
          toast.error(t("hotel.toast.elevatorError"));
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error(t("hotel.toast.elevatorSystemError"));
      })
      .finally(() => {
        setIsElevatorLoading(false);
      });
  }, [hotelId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadMyRoom();
  }, [loadMyRoom]);

  const enterInRoom = useCallback(
    async (id: number) => {
      if (id) {
        setIsEntering(true);

        await EstateMpProvider.enter(id)
          .then((response) => {
            if (response instanceof RpcMpProviderFailedResponse) {
              console.error(response);
              toast.error(t(`hotel.toast.enterError`));
            }
          })
          .catch((e) => {
            console.error(e);
            toast.error(t("hotel.toast.enterSystemError"));
          })
          .finally(() => {
            setIsEntering(false);
          });
      }
    },
    [t],
  );

  return {
    isMyRoomLoading,
    myRoom,
    data,
    isElevatorLoading,
    onClose,
    load,
    isEntering,
    enterInRoom,
  };
}
